import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * OAuth callback page — handles the redirect from Faceit/Steam OAuth.
 * Exchanges the one-time OAuth code for a JWT, fetches user data, and redirects.
 */
export function OAuthCallback() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { login } = useAuth();
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const code = searchParams.get('code');
        const errorParam = searchParams.get('error');

        if (errorParam) {
            const messages: Record<string, string> = {
                no_code: 'Authentication failed — no authorization code received.',
                faceit_failed: 'Faceit authentication failed. Please try again.',
                steam_failed: 'Steam authentication failed. Please try again.',
            };
            setError(messages[errorParam] || 'Authentication failed. Please try again.');
            return;
        }

        if (!code) {
            setError('No authentication code received.');
            return;
        }

        // Fetch user data with the token, then log in
        const authenticate = async () => {
            try {
                const exchange = await axios.post(`${API}/auth/exchange-code`, { code });
                const token = exchange.data?.access_token;
                if (!token) throw new Error('OAuth code is invalid or expired');
                const res = await axios.get(`${API}/users/me`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                login(token, res.data);
                navigate('/team-selection', { replace: true });
            } catch (err) {
                console.error('OAuth callback failed:', err);
                setError('Failed to verify your identity. Please try again.');
            }
        };

        authenticate();
    }, [searchParams, login, navigate]);

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #0a0e1a 0%, #111827 100%)',
            fontFamily: "'Inter', 'Segoe UI', sans-serif",
        }}>
            {error ? (
                <div style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '16px',
                    padding: '32px 40px',
                    textAlign: 'center',
                    maxWidth: '420px',
                }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                    <h2 style={{ color: '#ef4444', fontSize: '18px', fontWeight: 700, marginBottom: '12px' }}>
                        Authentication Failed
                    </h2>
                    <p style={{ color: '#94a3b8', fontSize: '14px', lineHeight: 1.6, marginBottom: '24px' }}>
                        {error}
                    </p>
                    <button
                        onClick={() => navigate('/', { replace: true })}
                        style={{
                            background: 'linear-gradient(135deg, #5f73ff 0%, #8b5cf6 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            padding: '12px 28px',
                            fontSize: '14px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                        }}
                        onMouseEnter={e => { (e.target as HTMLElement).style.transform = 'scale(1.02)'; }}
                        onMouseLeave={e => { (e.target as HTMLElement).style.transform = 'scale(1)'; }}
                    >
                        Back to Login
                    </button>
                </div>
            ) : (
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        border: '3px solid rgba(95, 115, 255, 0.2)',
                        borderTopColor: '#5f73ff',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                        margin: '0 auto 20px',
                    }} />
                    <p style={{ color: '#94a3b8', fontSize: '14px', fontWeight: 500 }}>
                        Verifying your identity...
                    </p>
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
            )}
        </div>
    );
}
